import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MailCheck } from "lucide-react";
import { toast, Toaster } from "sonner";
import BrandHeader from "../components/BrandHeader";
import { clientRequestReset } from "../lib/clientAuth";

// Request a password reset email. Always shows the same success screen
// regardless of whether the email is registered (no user enumeration).

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !email) return;
    setBusy(true);
    try {
      await clientRequestReset(email.trim());
      setSent(true);
    } catch (err) {
      // Still show the generic screen to avoid enumeration signals.
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen bg-brand-base flex flex-col">
      <Toaster richColors position="top-center" />
      <BrandHeader small />

      <main className="flex-1 px-6 md:px-12 lg:px-16 py-10 md:py-14 max-w-md mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {sent ? (
            <div data-testid="forgot-success">
              <MailCheck size={40} className="text-brand-primary" strokeWidth={1.5} />
              <h1 className="font-serif text-3xl text-brand-ink mt-4">Check your inbox</h1>
              <p className="mt-3 text-brand-ink-muted leading-relaxed">
                If <strong>{email}</strong> is a HER Aligned account, we've sent you a link to reset your password.
                The link expires in 30 minutes.
              </p>
              <p className="mt-6 text-sm text-brand-ink-muted">
                <Link to="/audit/login" className="text-brand-primary font-medium hover:underline">Back to sign in</Link>
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-3xl text-brand-ink">Reset your password</h1>
              <p className="mt-3 text-brand-ink-muted">Enter the email you signed up with and we'll send you a reset link.</p>
              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <label className="label-eyebrow block mb-2" htmlFor="forgot-email">Email</label>
                  <input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    className="input-warm text-lg"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={!email || busy}
                  className="btn-primary inline-flex items-center gap-2 w-full sm:w-auto disabled:opacity-50"
                  data-testid="forgot-submit-btn"
                >
                  {busy ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send reset link"}
                </button>
              </form>
              <p className="mt-8 text-sm text-brand-ink-muted">
                <Link to="/audit/login" className="text-brand-primary font-medium hover:underline">Back to sign in</Link>
              </p>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
